import React, { useState, useEffect } from 'react';
import { Modal, Alert } from 'antd';
import { AudioOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useRecorder } from '../../hooks/useRecorder';
import useRecordingTimer from '../../hooks/useRecordingTimer';
import { usePreventRecordingAbandon } from '../../hooks/usePreventRecordingAbandon';
import completeSession from '../../utils/session/completeSession';
import { displayWarningModal } from '../../utils/errorHandling';
import TestErrorComponent from './TestErrorComponent';
import { toast } from "react-hot-toast";

import PatientForm from '../../components/patient/PatientForm';
import RecordingTimerSection from './RecordingTimerSection';
import RecordingActions from './RecordingActions';
import MicrophoneError from './MicrophoneError';
import NoteTypeSelector from './NoteTypeSelector';
import { logEvent } from "../../analytics";
import { useNavigate } from 'react-router-dom';
import VisitModalContent from './VisitModalContent';
import confirmVisitDiscard from './VisitDiscardConfirm';

export default function RecordingModal({ 
  patient = null,
  fromPatientDetails = false,
  open = false,
  onClose
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showNoteTypeSelector, setShowNoteTypeSelector] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [recordingModalState, setRecordingModalState] = useState('initial');
  const [selectedPatient, setSelectedPatient] = useState(patient);
  const [editingPatient, setEditingPatient] = useState(null);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [showErrorButtons, setShowErrorButtons] = useState(false);

  const {
    elapsedTime,
    start: startTimer,
    pause: pauseTimer,
    reset: resetTimer
  } = useRecordingTimer();

  const {
    isRecording,
    toggleRecording,
    endRecording,
    sessionUuid,
    totalChunks,
    problemType,
    problemStats,
  } = useRecorder();

  usePreventRecordingAbandon(isRecording, sessionUuid);

  // Check for query parameter on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error_buttons') === '1') {
      setShowErrorButtons(true);
    }
  }, []);

  const handleStart = async () => {
    // Prevent multiple clicks
    if (isStarting) return;
    
    console.log('[RecordingModal] Starting recording process...');
    setIsStarting(true);
    
    try {
      console.log('[RecordingModal] Calling toggleRecording...');
      // Start the recording - this is the async operation that might take time
      await toggleRecording();
      console.log('[RecordingModal] toggleRecording completed successfully');
      
      // Update UI state immediately
      setRecordingModalState('recording');
      startTimer();
      setIsStarting(false);
      console.log('[RecordingModal] Recording started successfully');
    } catch (error) {
      console.error('[RecordingModal] Error during recording start:', error);
      setIsStarting(false);
    }
  };

  const handlePause = () => {
    console.log('[RecordingModal] Pausing recording...');
    requestAnimationFrame(() => {
      toggleRecording();
      setRecordingModalState('paused');
      pauseTimer();
      console.log('[RecordingModal] Recording paused');
    });
  };

  const handleResume = () => {
    console.log('[RecordingModal] Resuming recording...');
    requestAnimationFrame(() => {
      toggleRecording();
      setRecordingModalState('recording');
      startTimer();
      console.log('[RecordingModal] Recording resumed');
    });
  };

  const handleFinalize = async () => {
    setIsFinalizing(true);
    if (!sessionUuid || totalChunks < 1 || elapsedTime < 30) {
      displayWarningModal(
        t('session_recorder.errors.too_short_title'),
        t('session_recorder.errors.too_short_body'),
        'rec-too-short-warning-modal'
      );
      setIsFinalizing(false);
      return;
    }

    const success = await completeSession(sessionUuid, selectedPatient?.getUUID(), totalChunks, problemStats);
    // even if we couldn't complete the session, we still want to end the recording and close the modal, without showing error
    await endRecording();
    closeModal();
    if (success) {
      setShowNoteTypeSelector(true);
    } else {
      toast(t('session_recorder.errors.complete_warning'), {
        icon: '🛜' 
      });
    }
    logEvent('SessionRecorder', 'Complete');
    setIsFinalizing(false);
  };

  const handleCloseNoteTypeSelector = () => {
    setShowNoteTypeSelector(false);
  };

  const handleClose = async () => {
    // No session: just close immediately
    if (recordingModalState === 'initial' || !sessionUuid) {
      await endRecording();
      closeModal();
      return;
    }

    const confirmed = await confirmVisitDiscard({ sessionUuid, t });
    if (confirmed) {
      await endRecording();
      closeModal();
    }
  };

  const closeModal = async () => {
    if (isRecording) {
      await endRecording();
    }
    pauseTimer();
    setRecordingModalState('initial');
    resetTimer();
    setEditingPatient(null);
    onClose();
  };

  return (
    <>
      <Modal
        key="recording-modal"
        forceRender
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ width: '24px', display: 'inline-block' }}>
              {recordingModalState === 'recording' && <AudioOutlined className='iconSpacing' />}
            </span>
            {t(`recording_modal.${recordingModalState}_title`)}
          </div>
        }
        open={open}
        onCancel={handleClose}
        footer={null}
        width={800}
        destroyOnClose={false}
        closable={true}
        maskClosable={false}
        data-testid="recording-modal"
      >
        <VisitModalContent
          initialPatient={patient}
          sessionUuid={sessionUuid}
          fromPatientDetails={fromPatientDetails}
          onPatientChange={setSelectedPatient}
        >
          {/* Timer and error sections */}
          <div style={{ 
            display: recordingModalState !== 'initial' ? 'block' : 'none',
            minHeight: recordingModalState === 'initial' ? '100px' : '0',
          }}>
            <RecordingTimerSection elapsedTime={elapsedTime}/>
            <MicrophoneError type={problemType}/>
          </div>

          {/* Success alert */}
          <div 
            style={{
              textAlign: 'center', 
              marginTop: 0, 
              marginBottom: 10,
              display: recordingModalState === 'recording' && !problemType && elapsedTime < 5 ? 'block' : 'none',
              minHeight: '0px',
            }}
          >
            <Alert type='success' style={{fontSize: 16}} message={t('session_recorder.subtitle')} />
          </div>

          {/* Recording actions in a fixed-height container */}
          <div>
            <RecordingActions
              recordingModalState={recordingModalState}
              onStart={handleStart}
              onPause={handlePause}
              onResume={handleResume}
              onFinalize={handleFinalize}
              isFinalizing={isFinalizing}
              isStarting={isStarting}
            />
          </div>

          {showErrorButtons && (
            <TestErrorComponent />
          )}
        </VisitModalContent>
      </Modal>

      <PatientForm
        isOpen={isPatientModalOpen}
        editingPatient={editingPatient}
        onCancel={() => {
          setIsPatientModalOpen(false);
          setEditingPatient(null);
        }}
        onSave={(savedPatient) => {
          setSelectedPatient(savedPatient);
          setIsPatientModalOpen(false);
          setEditingPatient(null);
        }}
      />

      {showNoteTypeSelector && (
        (() => {
          console.log('[RecordingModal] Rendering NoteTypeSelector with patient:', selectedPatient);
          return (
            <NoteTypeSelector
              sessionUuid={sessionUuid}
              visible={showNoteTypeSelector}
              onClose={handleCloseNoteTypeSelector}
              patient={selectedPatient}
            />
          );
        })()
      )}
    </>
  );
}